import React, { useMemo, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import wqmsService from "../services/wqmsService";
import "./RiverParameterComparison.css"; // ✅ reuse your existing CSS file

dayjs.extend(utc);
dayjs.extend(timezone);

const TZ = "Asia/Karachi";

const RIVER_OPTIONS = ["Ravi", "Chenab", "Jhelum", "Sutlej", "Indus", "Punjnad"];
const DRAIN_OPTIONS = ["All", "Yes", "No"];

const intervals = [
  { value: "hourly", label: "Hourly" },
  { value: "weekly", label: "Weekly" },
  { value: "monthly", label: "Monthly" },
  { value: "yearly", label: "Yearly" },
];

// ✅ Base params (MEASURED keys). Clean keys are derived automatically.
const PARAM_BASE = [
  { key: "ph_avg", label: "pH" },
  { key: "temperature_c_avg", label: "Temperature (°C)" },
  { key: "dissolved_oxygen_mg_l_avg", label: "Dissolved Oxygen (mg/L)" },
  { key: "turbidity_ntu_avg", label: "Turbidity (NTU)" },
  { key: "conductivity_us_cm_avg", label: "Conductivity (µS/cm)" },
  //{ key: "level_m_avg", label: "Level (m) (avg)" },
  { key: "redox_mv_avg", label: "Redox/ORP (mV)" },

  { key: "ammonia_mg_l_avg", label: "Ammonia (NH3) (mg/L)" },
  { key: "bod_mg_l_avg", label: "BOD (mg/L)" },
  { key: "cod_mg_l_avg", label: "COD (mg/L)" },
  { key: "tss_mg_l_avg", label: "TSS (mg/L)" },
  { key: "toc_mg_l_avg", label: "TOC (mg/L)" },
  { key: "doc_mg_l_avg", label: "DOC (mg/L)" },
  { key: "chloride_mg_l_avg", label: "Chloride (mg/L)" },
  { key: "color_hazen_avg", label: "Color (Hazen)" },
];

const toCleanKey = (k) => {
  if (!k) return k;
  if (k.endsWith("_clean_avg")) return k;
  return k.replace(/_avg$/, "_clean_avg");
};

const toMeasuredKey = (k) => {
  if (!k) return k;
  if (k.endsWith("_avg") && !k.endsWith("_clean_avg")) return k;
  return k.replace(/_clean_avg$/, "_avg");
};

export default function RiverVsRiverComparison() {
  const navigate = useNavigate();

  // ✅ Rivers (multi)
  const [selectedRivers, setSelectedRivers] = useState(["Ravi", "Chenab"]);
  const [drain, setDrain] = useState("All");

  // ✅ Values + Chart Type
  const [valueMode, setValueMode] = useState("measured"); // clean | measured
  const [chartType, setChartType] = useState("line"); // line | bar

  // ✅ Params + Interval + Date range
  //const [selectedParameters, setSelectedParameters] = useState([toCleanKey("ph_avg")]);
  const [selectedParameters, setSelectedParameters] = useState(["ph_avg"]);
  const [interval, setInterval] = useState("hourly");

  const [fromDate, setFromDate] = useState(dayjs().tz(TZ).subtract(2, "day").format("YYYY-MM-DD"));
  const [toDate, setToDate] = useState(dayjs().tz(TZ).format("YYYY-MM-DD"));

  // ✅ Data
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // riverStations[riverName] = [stationIds...]
  const [riverStations, setRiverStations] = useState({});

  // seriesByParam[paramKey] = [{ ts, Ravi: avg, Chenab: avg, ... }]
  const [seriesByParam, setSeriesByParam] = useState({});

  // ✅ Param options based on valueMode
  const PARAM_OPTIONS = useMemo(() => {
    if (valueMode === "clean") {
      return PARAM_BASE.map((p) => ({
        key: toCleanKey(p.key),
        label: p.label.replace("(avg)", "(clean avg)"),
      }));
    }
    return PARAM_BASE;
  }, [valueMode]);

  const paramLabelByKey = useMemo(() => {
    const m = {};
    for (const p of PARAM_OPTIONS) m[p.key] = p.label;
    return m;
  }, [PARAM_OPTIONS]);

  // ✅ Send PKT timestamps WITHOUT Z/+05:00 (matches timestamp without time zone)
  const startISO = useMemo(() => `${fromDate}T00:00:00.000`, [fromDate]);
  const endISO = useMemo(() => `${toDate}T23:59:59.999`, [toDate]);

  // ✅ Display timestamps as PKT without shifting (+5 bug fix)
  const fmtTsPKT = (ts, fmt) => {
    if (!ts) return "";
    return dayjs.utc(ts).tz(TZ, true).format(fmt);
  };

  // ✅ River colors (repeatable palette)
  const RIVER_COLORS = useMemo(
    () => [
      "#60a5fa",
      "#f472b6",
      "#34d399",
      "#fbbf24",
      "#a78bfa",
      "#22d3ee",
      "#fb7185",
      "#4ade80",
      "#e879f9",
      "#fde047",
    ],
    []
  );

  const colorByRiver = useMemo(() => {
    const m = {};
    selectedRivers.forEach((r, i) => (m[r] = RIVER_COLORS[i % RIVER_COLORS.length]));
    return m;
  }, [selectedRivers, RIVER_COLORS]);

  // ✅ Load stations for selected rivers whenever selection/drain changes
  useEffect(() => {
    (async () => {
      try {
        setError("");
        if (!selectedRivers.length) {
          setRiverStations({});
          setSeriesByParam({});
          return;
        }

        const entries = await Promise.all(
          selectedRivers.map(async (r) => {
            const list = await wqmsService.getStations({
              timeRange: "all",
              force: true,
              river: r,
              drain,
            });
            const ids = (list || [])
              .map((s) => Number(s.station_id ?? s.id))
              .filter(Boolean);
            return [r, ids];
          })
        );

        const obj = {};
        for (const [r, ids] of entries) obj[r] = ids;

        setRiverStations(obj);
        setSeriesByParam({}); // clear old series when river selection changes
      } catch (e) {
        setError("Failed to load stations for selected rivers.");
        setRiverStations({});
        setSeriesByParam({});
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRivers.join("|"), drain]);

  // ✅ Tooltip
  const fmtVal = (v) => {
    if (v === null || v === undefined || Number.isNaN(v)) return "—";
    const n = Number(v);
    return Number.isFinite(n) ? n.toFixed(3) : String(v);
  };

  const CustomTooltip = ({ active, label, payload }) => {
    if (!active || !payload?.length) return null;

    const labelText = fmtTsPKT(label, "YYYY-MM-DD HH:mm");

    return (
      <div className="rpc-tooltip">
        <div className="rpc-tooltipDate">{labelText}</div>

        {payload.map((p) => (
          <div key={p.dataKey} className="rpc-tooltipRow">
            <span className="rpc-tooltipName" style={{ color: p.color }}>
              {p.name}
            </span>
            <span className="rpc-tooltipVal">{fmtVal(p.value)}</span>
          </div>
        ))}
      </div>
    );
  };

  const CustomLegend = ({ payload = [] }) => {
    return (
      <div className="rpc-legend">
        {payload.map((item) => (
          <span key={item.dataKey} className="rpc-legendItem" style={{ color: item.color }}>
            {item.value}
          </span>
        ))}
      </div>
    );
  };

  // ✅ Build river avg series from station rows (rows contain station_id + ts + value)
  const buildRiversAvgSeries = (riverRowsMap) => {
    const m = new Map();

    for (const riverName of Object.keys(riverRowsMap)) {
      const rows = riverRowsMap[riverName] || [];
      for (const r of rows) {
        const ts = r.ts;
        if (!ts) continue;

        const v = r.value;
        if (v === null || v === undefined || Number.isNaN(v)) continue;

        if (!m.has(ts)) m.set(ts, { ts, __tmp: {} });
        const o = m.get(ts);

        if (!o.__tmp[riverName]) o.__tmp[riverName] = { sum: 0, n: 0 };
        o.__tmp[riverName].sum += Number(v);
        o.__tmp[riverName].n += 1;
      }
    }

    const out = [];
    for (const o of m.values()) {
      const row = { ts: o.ts };
      for (const riverName of selectedRivers) {
        const s = o.__tmp[riverName];
        row[riverName] = s && s.n ? s.sum / s.n : null;
      }
      out.push(row);
    }

    out.sort((a, b) => new Date(a.ts) - new Date(b.ts));
    return out;
  };

  // ✅ Compare
  const fetchData = async () => {
    if (selectedRivers.length < 1) return setError("Select at least 1 river.");
    if (!selectedParameters.length) return setError("Select at least 1 parameter.");

    for (const r of selectedRivers) {
      if (!(riverStations[r] || []).length) {
        return setError(`No stations found for river: ${r}.`);
      }
    }

    setLoading(true);
    setError("");

    try {
      const out = {};

      for (const paramKey of selectedParameters) {
        const riverRowsMap = {};

        await Promise.all(
          selectedRivers.map(async (riverName) => {
            const stationIds = riverStations[riverName] || [];
            const rows = await wqmsService.getSeriesMultiStations({
              stationIds,
              param: paramKey,
              start: startISO,
              end: endISO,
              res: interval,
            });
            riverRowsMap[riverName] = rows || [];
          })
        );

        out[paramKey] = buildRiversAvgSeries(riverRowsMap);
      }

      setSeriesByParam(out);
    } catch (e) {
      console.error(e);
      setError("Failed to load River vs River comparison data.");
    } finally {
      setLoading(false);
    }
  };

  // ✅ Export CSV (all selected params, all selected rivers)
  const exportCSV = () => {
    const keys = selectedParameters.filter((k) => (seriesByParam[k] || []).length > 0);
    if (!keys.length) {
      setError("No data to export. Please Compare first.");
      return;
    }

    const esc = (val) => {
      const s = String(val ?? "");
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };

    const header = ["Parameter", "Timestamp (PKT)", ...selectedRivers];

    const rows = [];
    for (const paramKey of keys) {
      const label = paramLabelByKey[paramKey] || paramKey;
      for (const r of seriesByParam[paramKey]) {
        const line = [label, fmtTsPKT(r.ts, "YYYY-MM-DD HH:mm"), ...selectedRivers.map((rv) => r[rv])];
        rows.push(line.map(esc).join(","));
      }
    }

    const meta = [
      `ValueMode,${esc(valueMode)}`,
      `Rivers,${esc(selectedRivers.join(" | "))}`,
      `From (PKT),${esc(fromDate + " 00:00")}`,
      `To (PKT),${esc(toDate + " 23:59")}`,
      `Interval,${esc(interval)}`,
      `Drain,${esc(drain)}`,
      "",
    ].join("\n");

    const csv = [meta, header.map(esc).join(","), ...rows].join("\n");
    const filename = `RiverVsRiver_${valueMode}_${fromDate}_to_${toDate}_${interval}.csv`;

    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // ✅ Build charts list for all selected params
  const charts = useMemo(() => {
    return selectedParameters.map((k) => ({
      key: k,
      label: paramLabelByKey[k] || k,
      data: seriesByParam[k] || [],
    }));
  }, [selectedParameters, paramLabelByKey, seriesByParam]);

  // ✅ Summary
  const calcStats = (data, key) => {
    let min = null,
      max = null,
      sum = 0,
      n = 0;
    for (const r of data) {
      const v = r[key];
      if (v === null || v === undefined || Number.isNaN(v)) continue;
      min = min === null ? v : Math.min(min, v);
      max = max === null ? v : Math.max(max, v);
      sum += v;
      n += 1;
    }
    return { min, max, avg: n ? sum / n : null, points: n };
  };

  return (
    <div className="rpc-page">
      <div className="rpc-wrap">
        <div className="rpc-header">
          <button type="button" className="admin-btn admin-btn-secondary" onClick={() => navigate(-1)}>
            <ArrowLeft size={18} />
            Back
          </button>

          <div className="rpc-header-main">
            <h2 className="rpc-title">River vs River Comparison</h2>
            <p className="rpc-subtitle">
              Compares each river as <b>AVG across all stations</b> per timestamp (PKT).
            </p>
          </div>

          <div className="rpc-header-actions">
            <span className="rpc-pill">
              Rivers: <strong>{selectedRivers.length}</strong> · Params: <strong>{selectedParameters.length}</strong>
            </span>
          </div>
        </div>

        <div className="rpc-card rpc-controls rpc-controls--vvr">
          <div className="rpc-controls-head">Report filters</div>
          <div className="rpc-vvrGrid">
            {/* LEFT */}
            <div className="rpc-vvrLeft">
              <div className="rpc-vvrLeftGrid">
                <div className="rpc-field">
                  <label>Chart</label>
                  <select className="rpc-select" value={chartType} onChange={(e) => setChartType(e.target.value)}>
                    <option value="line">Line</option>
                    <option value="bar">Bar</option>
                  </select>
                </div>

{/*  <div className="rpc-field">
                  <label>Values</label>
                  <select
                    className="rpc-select"
                    value={valueMode}
                    onChange={(e) => {
                      const next = e.target.value;
                      setValueMode(next);
                      setSelectedParameters((prev) =>
                        prev.map((k) => (next === "clean" ? toCleanKey(k) : toMeasuredKey(k)))
                      );
                      setSeriesByParam({});
                    }}
                  >
                    <option value="clean">Clean</option>
                    <option value="measured">Measured</option>
                  </select>
                </div>
*/}

                <div className="rpc-field">
                  <label>On drain</label>
                  <select className="rpc-select" value={drain} onChange={(e) => setDrain(e.target.value)}>
                    {DRAIN_OPTIONS.map((d) => (
                      <option key={d} value={d}>
                        {d}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="rpc-field">
                  <label>Interval</label>
                  <select className="rpc-select" value={interval} onChange={(e) => setInterval(e.target.value)}>
                    {intervals.map((i) => (
                      <option key={i.value} value={i.value}>
                        {i.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="rpc-field">
                  <label>From</label>
                  <input className="rpc-input" type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
                </div>

                <div className="rpc-field">
                  <label>To</label>
                  <input className="rpc-input" type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
                </div>

                <div className="rpc-field rpc-vvrSpan2">
                  <label>Rivers (Multi)</label>
                  <select
                    className="rpc-multi rpc-multi--rivers"
                    multiple
                    value={selectedRivers}
                    onChange={(e) => setSelectedRivers(Array.from(e.target.selectedOptions).map((o) => o.value))}
                  >
                    {RIVER_OPTIONS.map((r) => (
                      <option key={r} value={r}>
                        {r}
                      </option>
                    ))}
                  </select>
                  <div className="rpc-hint">Hold Ctrl (Windows) / Cmd (Mac) for multi select</div>
                </div>
              </div>
            </div>

            {/* RIGHT */}
            <div className="rpc-vvrRight">
              <div className="rpc-field">
                <label>Parameters (Multi)</label>
                <select
                  className="rpc-multi rpc-multi--params"
                  multiple
                  value={selectedParameters}
                  onChange={(e) => setSelectedParameters(Array.from(e.target.selectedOptions).map((o) => o.value))}
                >
                  {PARAM_OPTIONS.map((p) => (
                    <option key={p.key} value={p.key}>
                      {p.label}
                    </option>
                  ))}
                </select>
                <div className="rpc-hint">Hold Ctrl to select multiple parameters</div>
              </div>
            </div>
          </div>

          <div className="rpc-controls-footer">
            <button className="rpc-btn" onClick={fetchData} disabled={loading}>
              {loading ? "Loading..." : "Compare"}
            </button>
          </div>

          {error ? <div className="rpc-alert">{error}</div> : null}
        </div>

        {/* Charts */}
        {charts.length === 0 ? (
          <div className="rpc-card rpc-empty">
            <strong>Select parameters and click Compare.</strong>
          </div>
        ) : (
          charts.map((c) => {
            return (
              <div key={c.key} className="rpc-card rpc-chart" style={{ marginBottom: 14 }}>
                <div className="rpc-chartHeader">
                  <div className="rpc-chartTitle">Parameter: {c.label}</div>
                  <div className="rpc-pill rpc-mono">
                    {fromDate} 00:00 → {toDate} 23:59 (PKT)
                  </div>
                </div>

                {c.data.length === 0 ? (
                  <div className="rpc-empty">
                    <div>
                      <strong>No data for this parameter</strong>
                      <div style={{ marginTop: 6, opacity: 0.8 }}>Try bigger date range or different interval.</div>
                    </div>
                  </div>
                ) : (
                  <div className="rpc-chartBox" style={{ height: 340 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      {chartType === "line" ? (
                        <LineChart data={c.data}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="ts" tickFormatter={(v) => fmtTsPKT(v, "MM-DD HH:mm")} minTickGap={24} />
                          <YAxis />
                          <Tooltip content={<CustomTooltip />} />

                          <Legend
                            content={<CustomLegend />}
                            payload={selectedRivers.map((rv) => ({
                              dataKey: rv,
                              value: rv,
                              color: colorByRiver[rv],
                            }))}
                          />

                          {selectedRivers.map((rv) => (
                            <Line
                              key={rv}
                              type="monotone"
                              dataKey={rv}
                              name={rv}
                              dot={false}
                              strokeWidth={2}
                              connectNulls
                              stroke={colorByRiver[rv]}
                              isAnimationActive={false}
                            />
                          ))}
                        </LineChart>
                      ) : (
                        <BarChart data={c.data}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="ts" tickFormatter={(v) => fmtTsPKT(v, "MM-DD HH:mm")} minTickGap={24} />
                          <YAxis />
                          <Tooltip content={<CustomTooltip />} />

                          <Legend
                            content={<CustomLegend />}
                            payload={selectedRivers.map((rv) => ({
                              dataKey: rv,
                              value: rv,
                              color: colorByRiver[rv],
                            }))}
                          />

                          {selectedRivers.map((rv) => (
                            <Bar key={rv} dataKey={rv} name={rv} fill={colorByRiver[rv]} />
                          ))}
                        </BarChart>
                      )}
                    </ResponsiveContainer>
                  </div>
                )}

                {/* Summary */}
                {c.data.length ? (
                  <div className="rpc-summary" style={{ marginTop: 10 }}>
                    <h3>Summary (AVG across stations)</h3>
                    <div className="rpc-tableWrap">
                      <table className="rpc-table">
                        <thead>
                          <tr>
                            <th>River</th>
                            <th>Min</th>
                            <th>Avg</th>
                            <th>Max</th>
                            <th>Points</th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedRivers.map((rv) => {
                            const st = calcStats(c.data, rv);
                            return (
                              <tr key={rv}>
                                <td className="rpc-mono">{rv}</td>
                                <td>{st.min ?? "-"}</td>
                                <td>{st.avg !== null ? st.avg.toFixed(3) : "-"}</td>
                                <td>{st.max ?? "-"}</td>
                                <td>{st.points}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ) : null}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
